import React, { useState, useEffect } from "react";
import { AuthGuard } from "@core/components/AuthGuard";
import { useRootLoaderData } from "@/root";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@core/components/ui/dialog";
import { Button } from "@core/components/ui/button";
import { Pencil } from "lucide-react";
import { useApi } from "@core/api";
import { Label } from "@core/components/ui/label";
import { Textarea } from "@core/components/ui/textarea";
import { useToast } from "@core/components/ui/use-toast";

interface ComponentMetadata {
  component: string;
  section: string;
  element: HTMLElement;
}

const ThemeDebugOverlayContent = () => {
  const { config, theme } = useRootLoaderData();
  const api = useApi();
  const { toast } = useToast();
  const [hoveredElement, setHoveredElement] = useState<HTMLElement | null>(null);
  const [metadata, setMetadata] = useState<ComponentMetadata | null>(null);
  const [hierarchy, setHierarchy] = useState<ComponentMetadata[]>([]);
  const [selectedHierarchy, setSelectedHierarchy] = useState<ComponentMetadata[] | null>(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [editingComponent, setEditingComponent] = useState<ComponentMetadata | null>(null);
  const [customClasses, setCustomClasses] = useState("");
  
  // Check if theme debug mode is enabled
  const themeConfig = (config?.Theme ?? {}) as any;
  const debugModeEnabled = themeConfig?.ThemeDebugMode === true;
  
  // Log debug status
  useEffect(() => {
    console.log('Theme Debug - debugModeEnabled:', debugModeEnabled);
  }, [debugModeEnabled]);
  
  // Don't render if not enabled
  if (!debugModeEnabled) {
    return null;
  }

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePos({ x: e.clientX, y: e.clientY });
      
      const target = e.target as HTMLElement;
      const themeComponent = target.closest('[data-theme-component]');
      
      if (themeComponent) {
        const element = themeComponent as HTMLElement;
        setHoveredElement(element);
        
        // Get immediate component metadata
        const componentName = element.getAttribute('data-theme-component');
        const sectionName = element.getAttribute('data-theme-section');
        
        if (componentName && sectionName) {
          setMetadata({
            component: componentName,
            section: sectionName,
            element
          });
          
          // Build hierarchy
          const hierarchyList: ComponentMetadata[] = [];
          let current: HTMLElement | null = element;
          
          while (current) {
            if (current.hasAttribute('data-theme-component')) {
              const comp = current.getAttribute('data-theme-component');
              const sect = current.getAttribute('data-theme-section');
              if (comp && sect) {
                hierarchyList.unshift({
                  component: comp,
                  section: sect,
                  element: current
                });
              }
            }
            current = current.parentElement;
          }
          
          setHierarchy(hierarchyList);
        }
      } else {
        setHoveredElement(null);
        setMetadata(null);
        setHierarchy([]);
      }
    };
    
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const themeComponent = target.closest('[data-theme-component]');
      
      if (themeComponent) {
        e.preventDefault();
        e.stopPropagation();
        
        // Rebuild hierarchy from current hover target
        const hierarchyList: ComponentMetadata[] = [];
        let current: HTMLElement | null = themeComponent as HTMLElement;
        
        while (current) {
          if (current.hasAttribute('data-theme-component')) {
            const comp = current.getAttribute('data-theme-component');
            const sect = current.getAttribute('data-theme-section');
            if (comp && sect) {
              hierarchyList.unshift({
                component: comp,
                section: sect,
                element: current
              });
            }
          }
          current = current.parentElement;
        }
        
        if (hierarchyList.length > 0) {
          setSelectedHierarchy(hierarchyList);
        }
      }
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('click', handleClick, true);
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('click', handleClick, true);
    };
  }, []);

  const handleClose = () => {
    setSelectedHierarchy(null);
  };

  const handleCopyComponentName = (componentName: string) => {
    navigator.clipboard.writeText(componentName);
  };

  const handleEditComponent = (comp: ComponentMetadata) => {
    // Get current CustomClasses value
    const currentValue = theme?.Sections?.[comp.section]?.[comp.component]?.CustomClasses || "";
    setCustomClasses(currentValue);
    setEditingComponent(comp);
    // Close the hierarchy panel
    setSelectedHierarchy(null);
  };

  const handleSaveCustomClasses = async () => {
    if (!editingComponent) return;

    try {
      const settingKey = `ThemeSettings:Sections:${editingComponent.section}:${editingComponent.component}:CustomClasses`;
      await api["/theme/update"]({ [settingKey]: customClasses.trim() || "" });

      // Update all matching elements in the DOM
      const allElements = document.querySelectorAll(
        `[data-theme-section="${editingComponent.section}"][data-theme-component="${editingComponent.component}"]`
      );

      allElements.forEach((element) => {
        const htmlElement = element as HTMLElement;
        // Get existing classes except theme-specific ones
        const existingClasses = Array.from(htmlElement.classList);
        
        // Reset to base classes and apply new custom classes
        htmlElement.className = customClasses.trim();
      });

      toast({
        title: "Theme Updated",
        description: `${editingComponent.component} styling updated successfully. Refreshing...`,
      });

      // Wait a moment for toast to show, then reload
      setTimeout(() => {
        window.location.reload();
      }, 1000);

    } catch (error) {
      console.error("Failed to save theme:", error);
      toast({
        title: "Save Failed",
        description: "Failed to save theme settings. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <>
      {/* Highlight overlay - follows hovered element */}
      {hoveredElement && !selectedHierarchy && (
        <div 
          style={{
            position: 'fixed',
            top: `${hoveredElement.getBoundingClientRect().top}px`,
            left: `${hoveredElement.getBoundingClientRect().left}px`,
            width: `${hoveredElement.offsetWidth}px`,
            height: `${hoveredElement.offsetHeight}px`,
            border: '2px solid #3b82f6',
            pointerEvents: 'none',
            zIndex: 999999,
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            transition: 'none' // Remove transition to make it more responsive
          }}
        />
      )}
      
      {/* Info tooltip - follows cursor */}
      {metadata && !selectedHierarchy && (
        <div 
          style={{
            position: 'fixed',
            left: mousePos.x + 20,
            top: mousePos.y + 20,
            zIndex: 999999,
            pointerEvents: 'none'
          }}
          className="bg-slate-900 text-white p-3 rounded-lg shadow-xl text-sm max-w-xs"
        >
          <div className="font-bold mb-1">{metadata.component}</div>
          <div className="text-xs text-slate-400">Section: {metadata.section}</div>
          {hierarchy.length > 1 && (
            <div className="text-xs text-blue-300 mt-2">
              +{hierarchy.length - 1} parent component{hierarchy.length > 2 ? 's' : ''} (click for details)
            </div>
          )}
        </div>
      )}
      
      {/* Hierarchy panel - shows on click */}
      {selectedHierarchy && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/50 z-[999998]"
            onClick={handleClose}
          />
          
          {/* Panel */}
          <div className="fixed left-4 top-20 bg-slate-900 text-white p-4 rounded-lg shadow-xl max-w-md z-[999999]">
            <div className="flex justify-between items-center mb-4">
              <div className="text-sm font-bold">Component Hierarchy</div>
              <button 
                onClick={handleClose}
                className="text-slate-400 hover:text-white text-xl leading-none"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-3">
              {selectedHierarchy.map((comp, idx) => (
                <div 
                  key={idx}
                  style={{ paddingLeft: `${idx * 16}px` }}
                  className="border-l-2 border-slate-700 pl-3"
                >
                  <div className="flex items-center justify-between gap-2 group">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{comp.component}</div>
                      <div className="text-xs text-slate-400">Section: {comp.section}</div>
                    </div>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => handleEditComponent(comp)}
                        className="text-xs bg-purple-600 hover:bg-purple-700 p-1.5 rounded"
                        title="Edit styling"
                      >
                        <Pencil className="h-3 w-3" />
                      </button>
                      <button
                        onClick={() => handleCopyComponentName(comp.component)}
                        className="text-xs bg-blue-600 hover:bg-blue-700 px-2 py-1 rounded"
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-4 pt-4 border-t border-slate-700 text-xs text-slate-400">
              Click the edit icon to modify component styling, or copy the name to find it in the admin theme editor.
            </div>
          </div>
        </>
      )}

      {/* Edit Dialog */}
      <Dialog open={!!editingComponent} onOpenChange={(open) => !open && setEditingComponent(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Component Styling</DialogTitle>
            <DialogDescription>
              Editing: <span className="font-mono font-bold">{editingComponent?.component}</span> in section <span className="font-mono">{editingComponent?.section}</span>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="custom-classes" className="text-sm font-medium">
                Custom Tailwind Classes
              </Label>
              <Textarea
                id="custom-classes"
                value={customClasses}
                onChange={(e) => setCustomClasses(e.target.value)}
                placeholder="e.g., flex items-center gap-2 bg-blue-500 hover:bg-blue-600"
                className="font-mono text-sm min-h-[150px] mt-2"
                rows={6}
              />
              <p className="text-xs text-muted-foreground mt-2">
                Enter space-separated Tailwind classes. Changes will be applied after saving and refreshing the page.
              </p>
            </div>

            {/* Live Preview */}
            {customClasses.trim() && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Preview</Label>
                <div className="p-4 bg-muted rounded-md border">
                  <div className={customClasses}>
                    <div className="text-sm font-medium">Sample Element</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      This shows how your custom classes will render
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingComponent(null)}>
              Cancel
            </Button>
            <Button onClick={handleSaveCustomClasses}>
              Save & Reload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const ThemeDebugOverlay = () => {
  return (
    <AuthGuard requiresRole="Global Admin" requiresPermission="accessAdminUI">
      <ThemeDebugOverlayContent />
    </AuthGuard>
  );
};
